import { Cancel, Save } from "@mui/icons-material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion, AccordionDetails, AccordionSummary, AppBar, Box, Button, Paper, Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, Toolbar, Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';
import Swal from "sweetalert2";
import { createMarketingInitiative, updateMarketingInitiative } from "../../../actions/marketingInitiativeActions";
import { apiV2 } from "../../../actions";
import LoadingBackdrop from "../../Common/LoadingBackdrop";
import InitiativeForm from "../../MarketingInitiative/InitiativeForm";
import { fetchInitiativeLogs } from "../../../services/marketingInitiatives";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";


export default function MarketingInitiativeForm() {
    const dispatch = useDispatch();
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [error, setError] = useState({
        name: false,
        code: false,
        default_unlock_duration: false,
        resources: false,
    });

    const [transactionHistoryExpanded, setTransactionHistoryExpanded] = useState(false);

    const [initiative, setInitiative] = useState({
        name: '',
        code: '',
        default_unlock_duration: '',
        description: '',
        global_campaign_lifespan: '',
        resources: [],
    });

    const [auditLogs, setAuditLogs] = useState([]);

    const handleTransactionHistoryChange = () => {
        setTransactionHistoryExpanded(!transactionHistoryExpanded);
    };

    const formatAction = (action) => {
        if (!action) return '';
        return action
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    useEffect(() => {
        if (id) {
            setLoading(true);
            apiV2.get(`/marketing-initiatives/${id}`)
                .then((response) => {
                    const data = response.data;
                    setInitiative({
                        ...data,
                        resources: (data.resources || []).map(r => ({
                            id: r.id,
                            name: r.name,
                            type: r.type,
                            key: `${r.type}-${r.id}`,
                        })),
                    });
                })
                .catch(() => {
                    Swal.fire({
                        icon: "error",
                        title: "Failed to load initiative",
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            handleReset();
        }
    }, [id]);

    useEffect(() => {
        handleFetchAuditLogs();
    }, [initiative.id]);

    const handleReset = () => {
        setInitiative({
            name: '',
            code: '',
            default_unlock_duration: '',
            description: '',
            global_campaign_lifespan: '',
            resources: [],
        });
    }

    const handleSave = () => {
        let error = {
            name: false,
            code: false,
            default_unlock_duration: false,
            resources: false,
        }

        if (!initiative.name || initiative.name.trim().length < 3) {
            error.name = true;
        }

        if (!initiative.code || initiative.code.trim().length < 3) {
            error.code = true;
        }

        if (!initiative.default_unlock_duration || parseInt(initiative.default_unlock_duration) < 0) {
            error.default_unlock_duration = true;
        }

        if (initiative.resources.length === 0) {
            error.resources = true;
        }

        if (error.name || error.code || error.default_unlock_duration || error.resources) {
            setError(error);
            Swal.fire({
                icon: "error",
                title: "Please fill all the required fields",
            });
            return;
        }

        if (!isNameValid(initiative.name)) {
            setError({
                ...error,
                name: true,
            });
            Swal.fire({
                icon: "error",
                title: "The title must contain only alphanumeric, space and dash characters",
            });
            return;
        }

        const initiativeData = {
            name: initiative.name,
            code: initiative.code,
            description: initiative.description,
            default_unlock_duration: parseInt(initiative.default_unlock_duration) || null,
            global_campaign_lifespan: parseInt(initiative.global_campaign_lifespan) || null,
            resources: initiative.resources.map(r => ({
                id: r.id,
                type: r.type,
            })),
        };

        if (initiative.id) {
            dispatch(updateMarketingInitiative(initiative.id, initiativeData, () => navigate('/marketing-initiatives')));
        } else {
            dispatch(createMarketingInitiative(initiativeData, () => navigate('/marketing-initiatives')));
        }
    }

    const isNameValid = (name) => {
        return /^[a-zA-Z0-9\s\-:]+$/.test(name);
    };

    const handleCancel = () => {
        if (isModified) {
            Swal.fire({
                icon: "warning",
                text: "You have unsaved changes. Are you sure you want to leave?",
                showConfirmButton: true,
                showCancelButton: true,
                reverseButtons: true,
                confirmButtonText: "Save & Close",
                cancelButtonText: "Close Without Saving",
            }).then((result) => {
                if (result.isConfirmed) {
                    handleSave();
                    return;
                }

                navigate('/marketing-initiatives');
            });
        } else {
            navigate('/marketing-initiatives');
        }
    }

    const handleFetchAuditLogs = async () => {
        if (!initiative.id) {
            return;
        }

        const logs = await fetchInitiativeLogs(initiative.id);
        setAuditLogs(logs);
    }

    return (
        <MainContentContainer>
            <LoadingBackdrop open={loading} />
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {initiative.id ? 'Edit Marketing Initiative' : 'Create New Marketing Initiative'}
                    </Typography>
                    <Button color="success" onClick={handleSave} startIcon={<Save />} variant="contained" sx={{ marginRight: 2 }}>
                        Save
                    </Button>
                    <Button color="error" onClick={handleCancel} startIcon={<Cancel />} variant="contained">
                        Cancel
                    </Button>
                </Toolbar>
            </AppBar>
            <Box gap={4} sx={{ mt: 2 }}>
                <Paper sx={{ p: 2 }}>
                    <InitiativeForm
                        initiative={initiative}
                        setInitiative={setInitiative}
                        error={error}
                        isModified={isModified}
                        setIsModified={setIsModified}
                    />
                </Paper>
            </Box>
            {
                auditLogs && auditLogs.length > 0 && (
                    <Box gap={4} sx={{ mt: 2 }}>
                        <Accordion expanded={transactionHistoryExpanded} onChange={handleTransactionHistoryChange}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="transaction-history-content"
                                id="transaction-history-header"
                                sx={{ backgroundColor: '#f0f0f0' }}
                            >
                                <Typography variant="h6">Transaction history</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ py: 2 }}>
                                    <TableContainer
                                        component={Paper}
                                        sx={{
                                            marginTop: 1,
                                            boxShadow: 2,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Table size="small" aria-label="Transaction history">
                                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableRow>
                                                    <TableCell>Timestamp</TableCell>
                                                    <TableCell>User</TableCell>
                                                    <TableCell>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {auditLogs.map((log) => (
                                                    <TableRow key={log.id}>
                                                        <TableCell component="th" scope="row">
                                                            {log.timestamp}
                                                        </TableCell>
                                                        <TableCell>{log.requested_by}</TableCell>
                                                        <TableCell>{formatAction(log.metadata?.action)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                )
            }
        </MainContentContainer>
    );
}
